/* eslint-disable no-magic-numbers */
import { DateTime } from "luxon";

import {
	resolveVirtualMeetingType
} from "../../../domain";

import {
	formFactory,
	standardTouchForm
} from "@teaming/common-ui";

const debugNamespace = "teaming:common-team:stores:stream:oneTimeMeetingForm";

const translationFactory = i18n => ( key, args = {} ) => i18n.global.t( `common-team.stores.oneTimeMeetingForm.${ key }`, args );

const fields = [
	"daysField",
	"documentName",
	"frequencyField",
	"linkField",
	"nameField",
	"meetingTemplateField",
	"meridiemField",
	"recurrenceId",
	"startDateField",
	"subDocId",
	"timeField",
	"timezoneField",
	"teamlessField",
];

// eslint-disable-next-line no-unused-vars
const emptyOneTimeMeetingForm = ( i18n, mergedOptions, mergedReadonlyData ) => {
	const { timezone, } = mergedReadonlyData;
	const now = DateTime.now().setZone( timezone );

	const emptyFields = fields.reduce( ( acc, field ) => {
		acc[ field ] = "";
		return acc;
	}, {} );

	const startOfNextHour = now.plus( { hours: 1, } )
		.startOf( "hour" );

	return {
		...emptyFields,
		daysField: [ startOfNextHour.weekdayLong.toLowerCase(), ],
		frequencyField: "onetime",
		meridiemField: startOfNextHour.toFormat( "a" ).toLowerCase(), // "am"|"pm"
		startDateField: startOfNextHour.toFormat( "yyyy-MM-dd" ),
		timeField: startOfNextHour.toFormat( "hh:mm" ),
		timezoneField: timezone,
		teamlessField: false,
	};
};

const LUXON_WEEK_DAYS = Object.freeze( {
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
	saturday: 6,
	sunday: 7,
} );

const PRIMEVUE_WEEK_DAYS = Object.freeze( {
	sunday: 0,
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
	saturday: 6,
} );

// eslint-disable-next-line max-statements
export const validateOneTimeMeetingForm = ( form, touches, i18n, mergedOptions, mergedReadonlyData ) => {
	const translate = translationFactory( i18n );
	const {
		meridiems,
		times,
		timezones,
	} = mergedReadonlyData;

	const errors = fields.reduce( ( acc, field ) => {
		acc[ field ] = "";
		return acc;
	}, {} );

	if ( touches.nameField ) {
		if ( !form.nameField.trim().length ) {
			errors.nameField = translate( "nameFieldMissing" );
		}
	}

	if ( touches.linkField ) {
		if ( !form.linkField.trim().length ) {
			errors.linkField = translate( "linkFieldMissing" );
		} else if ( !resolveVirtualMeetingType( form.linkField ) ) {
			errors.linkField = translate( "invalidLinkField" );
		}
	}

	if ( touches.timeField ) {
		if ( !form.timeField.trim().length ) {
			errors.timeField = translate( "timeFieldMissing" );
		} else if ( !times.includes( form.timeField ) ) {
			errors.timeField = translate( "invalidTimeField" );
		}
	}

	if ( touches.meridiemField ) {
		if ( !form.meridiemField.trim().length ) {
			errors.meridiemField = translate( "meridiemFieldMissing" );
		} else if ( !meridiems.includes( form.meridiemField ) ) {
			errors.meridiemField = translate( "invalidMeridiemField" );
		}
	}

	if ( touches.timezoneField ) {
		if ( !form.timezoneField.trim().length ) {
			errors.timezoneField = translate( "timezoneFieldMissing" );
		} else if ( !timezones.includes( form.timezoneField ) ) {
			errors.timezoneField = translate( "invalidTimezoneField" );
		}
	}

	if ( touches.startDateField ) {
		if ( !form.startDateField.trim().length ) {
			errors.startDateField = translate( "startDateFieldMissing" );
		}
		const now = DateTime.local( { zone: form.timeZoneField, } );
		const startDateTimeString = `${ form.startDateField } ${ form.timeField } ${ form.meridiemField }`;
		const startDateTime = DateTime.fromFormat( startDateTimeString, "yyyy-MM-dd hh:mm a", { zone: form.timezoneField, } );
		if ( startDateTime < now ) {
			errors.timeField = translate( "starTimeFieldInPast" );
		}
	}

	return errors;
};

const defaultOptions = {
	touchAll: false,
};

// eslint-disable-next-line new-cap
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const timezones = Intl.supportedValuesOf( "timeZone" );

const twelveHours = Array( 12 ).fill( 0 )
	.map( ( _, i ) => i < 9 ? `0${ i + 1 }` : `${ i + 1 }` );

const hourInFiveMinIncrements = Array( 12 ).fill( 0 )
	.map( ( _, i ) => i < 2 ? `0${ i * 5 }` : `${ i * 5 }` );

// 12 hours in 5 min increments
const times = twelveHours.reduce( ( acc, hour ) => {
	return [
		...acc,
		...hourInFiveMinIncrements.map( min => `${ hour }:${ min }` ),
	];
}, [] );

const defaultReadonlyData = {
	meridiems: Object.freeze( [
		"am",
		"pm",
	] ),
	times,
	timezone,
	timezones,
};

const makeForm = formFactory( {
	emptyForm: emptyOneTimeMeetingForm,
	touchForm: standardTouchForm( fields ),
	validateForm: validateOneTimeMeetingForm,
	defaultOptions,
	defaultReadonlyData,
	debugNamespace,
} );

export function newOneTimeMeetingForm( args ) {
	const form = makeForm( args );

	const disabledStartDays = Object.keys( PRIMEVUE_WEEK_DAYS ).reduce( ( acc, weekday ) => {
		if ( !form.daysField.includes( weekday ) ) {
			acc.push( PRIMEVUE_WEEK_DAYS[ weekday ] );
		}
		return acc;
	}, [] );

	const firstWeekdayName = form.daysField[ 0 ];
	const firstWeekdayNumber = LUXON_WEEK_DAYS[ firstWeekdayName ];

	const filteredStartDay = {
		ordinal: form.dayOffsetField,
		weekday: firstWeekdayNumber || 0,
	};

	form.disabledStartDays = disabledStartDays;
	form.filteredStartDay = filteredStartDay;

	return form;
}

export function oneTimeMeetingFromFields( form ) {
	const {
		recurrenceId: id,
		documentName,
		frequencyField: frequency,
		linkField: link,
		nameField: name,
		meetingTemplateField: meetingTemplateId,
		meridiemField: meridiem,
		startDateField: startDate,
		subDocId,
		timeField: time,
		timezoneField: _timezone,
		teamlessField,
	} = form;

	return {
		id,
		documentName,
		frequency,
		link,
		name,
		meetingTemplateId,
		meridiem,
		startDate,
		subDocId,
		time,
		timezone: _timezone,
		teamlessField
	};
}

export function oneTimeMeetingToFields( recurrence ) {
	const {
		documentName,
		frequency: frequencyField,
		link: linkField,
		meetingTemplateId: meetingTemplateField,
		meridiem: meridiemField,
		name: nameField,
		id: recurrenceId,
		startDate: startDateField,
		subDocId,
		time: timeField,
		timezone: timeZoneField,
		teamlessField,
	} = recurrence;

	return {
		documentName,
		frequencyField,
		linkField,
		meetingTemplateField,
		meridiemField,
		nameField,
		recurrenceId,
		startDateField,
		subDocId,
		timeField,
		timeZoneField,
		teamlessField,
	};
}
